import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../api/client'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Bell, TrendingUp, TrendingDown, Coins, ArrowRight } from 'lucide-react'
import { formatNumber, formatDateTime } from '../lib/utils'
import type { NotificationLog, NotificationStatistics } from '../types'

export function NotificationsPage() {
  const { t } = useTranslation()

  const [notifications, setNotifications] = useState<NotificationLog[]>([])
  const [statistics, setStatistics] = useState<NotificationStatistics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)

  const LIMIT = 20

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [notificationData, statsData] = await Promise.all([
        apiClient.getNotificationHistory(LIMIT, 0),
        apiClient.getNotificationStatistics(),
      ])
      setNotifications(notificationData.history)
      setStatistics(statsData)
      setHasMore(notificationData.history.length === LIMIT)
    } catch (error) {
      console.error('Failed to fetch notification data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadMore = async () => {
    try {
      const offset = (page + 1) * LIMIT
      const data = await apiClient.getNotificationHistory(LIMIT, offset)
      setNotifications((prev) => [...prev, ...data.history])
      setHasMore(data.history.length === LIMIT)
      setPage((prev) => prev + 1)
    } catch (error) {
      console.error('Failed to load more notifications:', error)
    }
  }

  const getDetectionModeLabel = (mode: string): string => {
    const labels: Record<string, string> = {
      start: '스타트 샷',
      stop: '스탑 샷',
      shot: '싸이클링 샷',
      cycling_start: '싸이클링 시작',
      cycling_end: '싸이클링 종료',
    }
    return labels[mode] || mode
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">{t('notifications.title')}</h1>
        <p className="text-light-text-muted dark:text-dark-text-muted mt-1">
          알림 발송 내역 및 통계를 확인하세요
        </p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                {t('notifications.totalSent')}
              </p>
              <p className="text-2xl font-bold mt-1">
                {formatNumber(statistics?.totalSent || 0)}
              </p>
            </div>
            <div className="p-3 bg-primary/10 rounded-lg">
              <Bell className="w-6 h-6 text-primary" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                {t('notifications.successful')}
              </p>
              <p className="text-2xl font-bold mt-1 text-success">
                {formatNumber((statistics?.totalSent || 0) - (statistics?.totalFailed || 0))}
              </p>
            </div>
            <div className="p-3 bg-success/10 rounded-lg">
              <TrendingUp className="w-6 h-6 text-success" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                {t('notifications.failedCount')}
              </p>
              <p className="text-2xl font-bold mt-1 text-danger">
                {formatNumber(statistics?.totalFailed || 0)}
              </p>
            </div>
            <div className="p-3 bg-danger/10 rounded-lg">
              <TrendingDown className="w-6 h-6 text-danger" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                {t('notifications.pointsUsed')}
              </p>
              <p className="text-2xl font-bold mt-1">
                {formatNumber(statistics?.totalPointsUsed || 0)}P
              </p>
            </div>
            <div className="p-3 bg-warning/10 rounded-lg">
              <Coins className="w-6 h-6 text-warning" />
            </div>
          </div>
        </Card>
      </div>

      {/* Detection Mode Breakdown */}
      {statistics?.byDetectionMode && Object.keys(statistics.byDetectionMode).length > 0 && (
        <Card title="감지 모드별 통계">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(statistics.byDetectionMode).map(([mode, count]) => (
              <div
                key={mode}
                className="p-4 rounded-lg bg-light-bg-secondary dark:bg-dark-bg-secondary text-center"
              >
                <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                  {getDetectionModeLabel(mode)}
                </p>
                <p className="text-xl font-bold mt-1">{formatNumber(count)}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Notification History */}
      <Card title={t('notifications.history')}>
        {notifications.length === 0 ? (
          <div className="text-center py-8 text-light-text-muted dark:text-dark-text-muted">
            <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>{t('notifications.noNotifications')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-light-border dark:border-dark-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                      {t('notifications.date')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                      감지 모드
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                      {t('notifications.type')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                      {t('notifications.status')}
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-light-text-muted dark:text-dark-text-muted">
                      포인트
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map((notification) => (
                    <tr
                      key={notification.notificationId}
                      className="border-b border-light-border dark:border-dark-border last:border-0"
                    >
                      <td className="py-3 px-4 text-sm">
                        {formatDateTime(notification.sentAt)}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {getDetectionModeLabel(notification.detectionMode)}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {notification.is_fallback ? (
                          <Badge variant="warning">{t('notifications.fallback')}</Badge>
                        ) : (
                          <Badge variant="info">{t('notifications.alimtalk')}</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <Badge
                          variant={
                            notification.status === 'sent' || notification.status === 'delivered'
                              ? 'success'
                              : notification.status === 'pending'
                              ? 'warning'
                              : 'danger'
                          }
                        >
                          {notification.status === 'sent'
                            ? t('notifications.sent')
                            : notification.status === 'delivered'
                            ? t('notifications.delivered')
                            : notification.status === 'pending'
                            ? t('notifications.pendingStatus')
                            : t('notifications.failedStatus')}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-right font-medium text-danger">
                        -{formatNumber(notification.pointsUsed)}P
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <div className="mt-4 text-center">
                <button
                  onClick={loadMore}
                  className="btn btn-secondary inline-flex items-center gap-2"
                >
                  더 보기 <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  )
}
